import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { MOCK_NURSE } from '../constants/mockNurse';
import { createAdmittedPatient } from '../utils/patientUtils';
import { getInitials, getNurseDisplayName } from '../i18n/translations';
import { createTaskId } from '../utils/taskUtils';
import { createFluidBalanceTask, mergePatientTasks } from '../utils/taskSeeder';
import {
  buildMergedFluidData,
  calculateFluidBalanceForCalendarDay,
} from '../utils/flowSheetData';
import { resolvePatientWardType } from '../utils/patientUtils';
import { getTaskIdsCompletedByNote, TYPE_LEVEL_NOTE_TYPES, getTypeLevelTaskIdsCompletedByNote } from '../utils/taskCompletionFromNote';
import { getShiftBlockIdForTask } from '../utils/shiftTimeBlocks';
import { attachAddendumToNote, createAddendum } from '../utils/addendum';
import {
  attachAddendumToCarePlan,
  createCarePlanAddendum,
  createCarePlanRecord,
} from '../utils/carePlan';
import { withRequiresDocumentation, createEmptyTaskDocumentationDraft, refreshTaskDocumentationDraftFromTask } from '../utils/taskDocumentation';
import { persistClinicalNote, saveClinicalNoteMetrics } from '../services/persistClinical';

const SessionContext = createContext(null);
const SESSION_KEY = 'niva_session_notes';
const FAVORITES_KEY = 'niva_favorites';
const PATIENT_STATUS_KEY = 'niva_patient_status';
const FLOW_SHEET_KEY = 'niva_flow_sheet';
const ADMITTED_PATIENTS_KEY = 'niva_admitted_patients';
const SHIFT_HANDOVER_KEY = 'niva_shift_handover_drafts';
const TASKS_KEY = 'niva_patient_tasks';
const CARE_PLANS_KEY = 'niva_patient_care_plans';

const EMPTY_FLOW_SHEET = {
  fluidInputs: [],
  fluidOutputs: [],
  manualMedications: [],
  manualObservations: [],
};

function createFlowSheetEntryId() {
  return `fs-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function loadJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

function normalizeNoteType(noteType) {
  if (noteType === 'mar') return 'medication_admin';
  return noteType;
}

function getNoteContent(note) {
  return note?.openaiNote || note?.note_content || {};
}

function updateTasksMap(prev, patientId, nextTasks) {
  return { ...(prev || {}), [patientId]: nextTasks };
}

function normalizedText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06FF\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractMeaningfulTokens(value) {
  const stop = new Set([
    'mg',
    'g',
    'mcg',
    'ml',
    'iv',
    'po',
    'im',
    'sc',
    'tab',
    'tablet',
    'capsule',
    'caps',
    'dose',
    'medication',
    'medications',
    'recording',
  ]);
  return normalizedText(value)
    .split(' ')
    .filter((token) => token.length > 2 && !stop.has(token));
}

function taskMatchesTranscript(task, transcriptText) {
  const tx = normalizedText(transcriptText);
  if (!tx) return false;
  const tokens = extractMeaningfulTokens(task.itemLabel || task.title);
  if (!tokens.length) return false;
  return tokens.some((token) => tx.includes(token));
}

function areCarriedTaskEquivalent(existingTask, carriedTask) {
  if (existingTask.type !== 'generated' || carriedTask.type !== 'generated') return false;
  if (!existingTask.title || !carriedTask.title) return false;
  if (existingTask.title.trim().toLowerCase() !== carriedTask.title.trim().toLowerCase()) return false;
  const existingDue = existingTask.dueTime ? new Date(existingTask.dueTime).getTime() : null;
  const carriedDue = carriedTask.dueTime ? new Date(carriedTask.dueTime).getTime() : null;
  return existingDue === carriedDue;
}

function carryOverFutureTasks(existingTasks, newShiftStart) {
  return (existingTasks || [])
    .filter((t) => {
      if (t.type !== 'generated') return false;
      if (!t.dueTime) return false;
      if (t.status === 'complete') return false;
      return new Date(t.dueTime) > new Date(newShiftStart);
    })
    .map((t) => ({
      ...t,
      id: createTaskId(),
      status:
        t.windowEnd && new Date(t.windowEnd) < new Date() ? 'overdue' : 'pending',
      fromPreviousShift: true,
    }));
}

export function SessionProvider({ children }) {
  const { nurseName } = useAuth();
  const { language, t } = useLanguage();
  const [sessionNotes, setSessionNotes] = useState(() => loadJson(SESSION_KEY, []));
  const [favoriteIds, setFavoriteIds] = useState(() => loadJson(FAVORITES_KEY, ['p1', 'p3', 'p7']));
  const [documentedPatientIds, setDocumentedPatientIds] = useState(() =>
    loadJson(PATIENT_STATUS_KEY, ['p2', 'p5'])
  );
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const [draftGeneration, setDraftGeneration] = useState(null);
  const [flowSheetByPatient, setFlowSheetByPatient] = useState(() =>
    loadJson(FLOW_SHEET_KEY, {})
  );
  const [admittedPatients, setAdmittedPatients] = useState(() =>
    loadJson(ADMITTED_PATIENTS_KEY, [])
  );
  const [tasksByPatient, setTasksByPatient] = useState(() => loadJson(TASKS_KEY, {}));
  const [carePlansByPatient, setCarePlansByPatient] = useState(() => loadJson(CARE_PLANS_KEY, {}));
  const [shiftHandoverDrafts, setShiftHandoverDraftsState] = useState(() =>
    loadJson(SHIFT_HANDOVER_KEY, [])
  );
  const [handoverRecordingTarget, setHandoverRecordingTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [taskDocSession, setTaskDocSession] = useState(null);
  const taskDocTranscriptHandlerRef = useRef(null);

  const registerTaskDocTranscriptHandler = useCallback((handler) => {
    taskDocTranscriptHandlerRef.current = handler;
    return () => {
      if (taskDocTranscriptHandlerRef.current === handler) {
        taskDocTranscriptHandlerRef.current = null;
      }
    };
  }, []);

  const submitTaskDocTranscript = useCallback((transcript) => {
    const handler = taskDocTranscriptHandlerRef.current;
    if (typeof handler === 'function') {
      handler(transcript);
      return true;
    }
    return false;
  }, []);

  const openTaskDocumentation = useCallback((patientId, task) => {
    if (!patientId || !task?.id) return;
    setTaskDocSession((prev) => {
      if (prev?.patientId === patientId && prev?.taskId === task.id) {
        return {
          ...prev,
          task,
          open: true,
          draft: refreshTaskDocumentationDraftFromTask(
            prev.draft || createEmptyTaskDocumentationDraft(task),
            task
          ),
        };
      }
      return {
        patientId,
        taskId: task.id,
        task,
        draft: createEmptyTaskDocumentationDraft(task),
        open: true,
      };
    });
  }, []);

  const setTaskDocumentationDraft = useCallback((draft) => {
    setTaskDocSession((prev) => {
      if (!prev) return prev;
      return { ...prev, draft: draft || null, open: true };
    });
  }, []);

  const setTaskDocumentationNotes = useCallback((generatedNotes) => {
    setTaskDocSession((prev) => {
      if (!prev?.draft) return prev;
      return {
        ...prev,
        draft: {
          ...prev.draft,
          generatedNotes,
          openaiNote: generatedNotes?.[0]?.note || prev.draft.openaiNote || null,
        },
      };
    });
  }, []);

  const closeTaskDocumentation = useCallback(() => {
    setTaskDocSession((prev) => (prev ? { ...prev, open: false } : null));
  }, []);

  const clearTaskDocumentation = useCallback(() => {
    setTaskDocSession(null);
  }, []);

  const addAdmittedPatient = useCallback(({ name, dob, sex, wardType }) => {
    const patient = createAdmittedPatient({ name, dob, sex, wardType });
    setAdmittedPatients((prev) => {
      const updated = [...prev, patient];
      saveJson(ADMITTED_PATIENTS_KEY, updated);
      return updated;
    });
    return patient;
  }, []);

  const getFlowSheetData = useCallback(
    (patientId) => flowSheetByPatient[patientId] || { ...EMPTY_FLOW_SHEET },
    [flowSheetByPatient]
  );

  const updateFlowSheetData = useCallback((patientId, updater) => {
    setFlowSheetByPatient((prev) => {
      const current = prev[patientId] || { ...EMPTY_FLOW_SHEET };
      const nextPatient = typeof updater === 'function' ? updater(current) : updater;
      const updated = { ...prev, [patientId]: nextPatient };
      saveJson(FLOW_SHEET_KEY, updated);
      return updated;
    });
  }, []);

  const recalculateFluidTaskSummary = useCallback(
    (patientId, nextFlowSheetData) => {
      const merged = buildMergedFluidData(
        sessionNotes,
        patientId,
        null,
        nextFlowSheetData || EMPTY_FLOW_SHEET
      );
      const inputs = merged.fluidInputs || [];
      const outputs = merged.fluidOutputs || [];
      const dayBalance = calculateFluidBalanceForCalendarDay(inputs, outputs, new Date());
      const totalInput = dayBalance.totalInput;
      const totalOutput = dayBalance.totalOutput;
      const netBalance = dayBalance.net;
      const entryCount = dayBalance.entryCount;

      setTasksByPatient((prevMap) => {
        const current = prevMap?.[patientId] || [];
        const fluidTaskIndex = current.findIndex((t) => t.type === 'fluid_balance');
        const baseFluidTask =
          fluidTaskIndex >= 0
            ? current[fluidTaskIndex]
            : createFluidBalanceTask(patientId, new Date());

        const updatedFluidTask = {
          ...baseFluidTask,
          status:
            baseFluidTask.status === 'complete'
              ? 'complete'
              : entryCount > 0
                ? 'in_progress'
                : 'pending',
          completedAt: baseFluidTask.status === 'complete' ? baseFluidTask.completedAt : null,
          fluidSummary: {
            totalInput,
            totalOutput,
            netBalance,
            entryCount,
            dayKey: dayBalance.dayKey,
            dayLabel: dayBalance.dayLabel,
          },
        };

        if (fluidTaskIndex >= 0) {
          const nextTasks = current.map((task, idx) => (idx === fluidTaskIndex ? updatedFluidTask : task));
          const updatedMap = updateTasksMap(prevMap, patientId, nextTasks);
          saveJson(TASKS_KEY, updatedMap);
          return updatedMap;
        }

        const updatedMap = updateTasksMap(prevMap, patientId, [...current, updatedFluidTask]);
        saveJson(TASKS_KEY, updatedMap);
        return updatedMap;
      });
    },
    [sessionNotes]
  );

  useEffect(() => {
    const patientIds = new Set([
      ...Object.keys(flowSheetByPatient || {}),
      ...(admittedPatients || []).map((patient) => patient.id),
    ]);
    patientIds.forEach((patientId) => {
      recalculateFluidTaskSummary(
        patientId,
        flowSheetByPatient?.[patientId] || EMPTY_FLOW_SHEET
      );
    });
  }, [admittedPatients, flowSheetByPatient, recalculateFluidTaskSummary, sessionNotes]);

  const addFluidInput = useCallback(
    (patientId, entry) => {
      updateFlowSheetData(patientId, (current) => ({
        ...current,
        fluidInputs: [...current.fluidInputs, { id: createFlowSheetEntryId(), ...entry }],
      }));
    },
    [updateFlowSheetData]
  );

  const addFluidOutput = useCallback(
    (patientId, entry) => {
      updateFlowSheetData(patientId, (current) => ({
        ...current,
        fluidOutputs: [...current.fluidOutputs, { id: createFlowSheetEntryId(), ...entry }],
      }));
    },
    [updateFlowSheetData]
  );

  const markFluidBalanceTaskComplete = useCallback(
    (patientId) => {
      const completedAt = new Date().toISOString();
      setTasksByPatient((prevMap) => {
        const tasks = prevMap?.[patientId] || [];
        const nextTasks = tasks.map((task) =>
          task.type === 'fluid_balance'
            ? {
                ...task,
                status: 'complete',
                completedAt,
              }
            : task
        );
        const updatedMap = updateTasksMap(prevMap, patientId, nextTasks);
        saveJson(TASKS_KEY, updatedMap);
        return updatedMap;
      });
    },
    []
  );

  const addManualMedication = useCallback(
    (patientId, entry) => {
      updateFlowSheetData(patientId, (current) => ({
        ...current,
        manualMedications: [...current.manualMedications, { id: createFlowSheetEntryId(), ...entry }],
      }));
    },
    [updateFlowSheetData]
  );

  const addManualObservation = useCallback(
    (patientId, entry) => {
      updateFlowSheetData(patientId, (current) => ({
        ...current,
        manualObservations: [...current.manualObservations, { id: createFlowSheetEntryId(), ...entry }],
      }));
    },
    [updateFlowSheetData]
  );

  const setShiftHandoverDrafts = useCallback((drafts) => {
    setShiftHandoverDraftsState(drafts);
    saveJson(SHIFT_HANDOVER_KEY, drafts);
  }, []);

  const updatePatientHandoverDraft = useCallback((patientKey, updater) => {
    setShiftHandoverDraftsState((prev) => {
      const updated = prev.map((draft) => {
        if (draft.patientKey !== patientKey) return draft;
        return typeof updater === 'function' ? updater(draft) : { ...draft, ...updater };
      });
      saveJson(SHIFT_HANDOVER_KEY, updated);
      return updated;
    });
  }, []);

  const clearHandoverRecordingTarget = useCallback(() => {
    setHandoverRecordingTarget(null);
  }, []);

  const clearShiftHandoverDrafts = useCallback(() => {
    setShiftHandoverDrafts([]);
  }, [setShiftHandoverDrafts]);

  const showToast = useCallback((nextToast) => {
    setToast(nextToast);
  }, []);

  const clearToast = useCallback(() => {
    setToast(null);
  }, []);

  const markAllPatientsShiftEnded = useCallback(() => {
    const endedAt = new Date().toISOString();
    setAdmittedPatients((prev) => {
      const updated = (prev || []).map((patient) => ({
        ...patient,
        currentShiftEnded: true,
        currentShiftEndedAt: endedAt,
      }));
      saveJson(ADMITTED_PATIENTS_KEY, updated);
      return updated;
    });
  }, []);

  const getPatientTasks = useCallback(
    (patientId) => (patientId ? tasksByPatient?.[patientId] || [] : []),
    [tasksByPatient]
  );

  const setPatientTasks = useCallback((patientId, updater) => {
    if (!patientId) return;
    setTasksByPatient((prev) => {
      const current = prev?.[patientId] || [];
      const next = typeof updater === 'function' ? updater(current) : updater;
      const updated = updateTasksMap(prev, patientId, next);
      saveJson(TASKS_KEY, updated);
      return updated;
    });
  }, []);

  const addTaskToPatient = useCallback(
    (patientId, task) => {
      setPatientTasks(patientId, (prev) => [...prev, task]);
    },
    [setPatientTasks]
  );

  const completeTask = useCallback(
    (patientId, taskId, completedAt, completedByNoteId = null, meta = null) => {
      setPatientTasks(patientId, (prev) =>
        {
          let completedTaskMeta = null;
          const next = prev.map((t) => {
            if (t.id !== taskId) return t;
            completedTaskMeta = {
              id: t.id,
              title: t.title,
              itemLabel: t.itemLabel,
              dueTime: t.dueTime,
              previousStatus: t.status,
            };
            return {
              ...t,
              status: 'complete',
              completedAt: completedAt || new Date().toISOString(),
              completedByNoteId,
              ...(meta?.completionOutcome
                ? {
                    completionOutcome: meta.completionOutcome,
                    deviationReason: meta.deviationReason || null,
                  }
                : {}),
            };
          });

          const vitalsPending = next
            .filter((t) => t.sourceNoteType === 'vitals' && (t.status === 'pending' || t.status === 'overdue'))
            .map((t) => ({ id: t.id, itemLabel: t.itemLabel, dueTime: t.dueTime, status: t.status }));
          const vitalsCompleted = next
            .filter((t) => t.sourceNoteType === 'vitals' && t.status === 'complete')
            .map((t) => ({ id: t.id, itemLabel: t.itemLabel, dueTime: t.dueTime, completedAt: t.completedAt }));

          console.log('[TaskDebug] completeTask:applied', {
            patientId,
            completedTaskMeta,
            completedByNoteId,
            meta,
            vitalsPending,
            vitalsCompleted,
          });

          return next;
        }
      );
    },
    [setPatientTasks]
  );

  const checkAndCompleteTask = useCallback(
    (patientId, noteType, noteTimestamp, noteId, note = null) => {
      const type = normalizeNoteType(noteType);
      if (type === 'medication_admin') {
        return;
      }
      const noteTime = new Date(noteTimestamp);
      const transcriptText =
        note?.transcript_cleaned || note?.transcript_raw || note?.transcript || '';
      console.log('[TaskDebug] checkAndCompleteTask:start', {
        patientId,
        noteType: type,
        noteId,
        noteTimestamp,
        transcriptPreview: String(transcriptText || '').slice(0, 120),
      });

      // Progress note / handover / etc.: match by note type within the shift block,
      // not only the narrow ±due window (so afternoon docs complete afternoon tasks).
      if (TYPE_LEVEL_NOTE_TYPES.has(type)) {
        const typeLevelIds = getTypeLevelTaskIdsCompletedByNote(
          tasksByPatient?.[patientId] || [],
          type,
          noteTime
        );
        console.log('[TaskDebug] checkAndCompleteTask:typeLevel', typeLevelIds);
        typeLevelIds.forEach((taskId) => {
          completeTask(patientId, taskId, noteTimestamp, noteId);
        });
        return;
      }

      const inWindow = (tasksByPatient?.[patientId] || []).filter(
        (t) =>
          (t.status === 'pending' || t.status === 'overdue') && t.sourceNoteType === type
      );
      console.log('[TaskDebug] checkAndCompleteTask:inWindow', inWindow.map((t) => ({
        id: t.id,
        title: t.title,
        itemLabel: t.itemLabel,
        dueTime: t.dueTime,
        status: t.status,
      })));

      const candidates = inWindow.filter((task) => {
        if (!task.windowStart || !task.windowEnd) return false;
        const start = new Date(task.windowStart);
        const end = new Date(task.windowEnd);
        if (task.status === 'overdue') {
          return noteTime >= start;
        }
        return noteTime >= start && noteTime <= end;
      });
      console.log('[TaskDebug] checkAndCompleteTask:candidates', candidates.map((t) => ({
        id: t.id,
        title: t.title,
        itemLabel: t.itemLabel,
      })));
      if (!candidates.length) return;

      const noteContent = getNoteContent(note);
      const matched = candidates.filter((task) => {
        if (type === 'medication_admin') {
          const medTokens = new Set();
          const meds = noteContent?.medications_administered || noteContent?.medications || [];
          meds.forEach((med) => {
            extractMeaningfulTokens(med.drug_name || med.name || '').forEach((token) => {
              medTokens.add(token);
            });
          });
          if (medTokens.size) {
            const taskTokens = extractMeaningfulTokens(task.itemLabel || task.title);
            return taskTokens.some((token) => medTokens.has(token));
          }
        }
        if (task.itemLabel || type === 'medication_admin') {
          return taskMatchesTranscript(task, transcriptText);
        }
        return true;
      });
      console.log('[TaskDebug] checkAndCompleteTask:matched', matched.map((t) => ({
        id: t.id,
        title: t.title,
        itemLabel: t.itemLabel,
      })));
      if (!matched.length) return;

      const groups = new Map();
      matched.forEach((task) => {
        const groupKey = task.itemLabel || task.title || task.sourceNoteType || 'task';
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey).push(task);
      });

      groups.forEach((groupTasks) => {
        const closest = [...groupTasks].sort((a, b) => {
          const aDue = a.dueTime ? new Date(a.dueTime).getTime() : noteTime.getTime();
          const bDue = b.dueTime ? new Date(b.dueTime).getTime() : noteTime.getTime();
          return Math.abs(aDue - noteTime.getTime()) - Math.abs(bDue - noteTime.getTime());
        })[0];
        if (closest) {
          console.log('[TaskDebug] checkAndCompleteTask:complete', {
            taskId: closest.id,
            taskTitle: closest.title,
            itemLabel: closest.itemLabel,
            shiftBlock: getShiftBlockIdForTask(closest, noteTime),
          });
          completeTask(patientId, closest.id, noteTimestamp, noteId);
        }
      });
    },
    [completeTask, tasksByPatient]
  );

  const ensureDefaultRecurringTasks = useCallback(
    (patient, shiftStartTime = new Date(), options = {}) => {
      if (!patient?.id) return;
      const patientId = patient.id;
      const patientWithWard = {
        ...patient,
        wardType: resolvePatientWardType(patient),
      };
      const carryFromPreviousShift = options.carryFromPreviousShift === true;

      setPatientTasks(patientId, (prev) => {
        const existing = prev || [];
        const merged = mergePatientTasks(existing, patientWithWard, shiftStartTime);
        if (!carryFromPreviousShift) return merged;

        const carriedTasks = carryOverFutureTasks(existing, shiftStartTime);
        const carriedOnly = carriedTasks.filter(
          (task) => !merged.some((existingTask) => areCarriedTaskEquivalent(existingTask, task))
        );
        return [...merged, ...carriedOnly];
      });
    },
    [setPatientTasks]
  );

  const addAddendum = useCallback((noteId, payload) => {
    let saved = null;
    setSessionNotes((prev) => {
      const index = prev.findIndex((n) => n.id === noteId);
      if (index < 0) return prev;
      const target = prev[index];
      const addendum = createAddendum({
        originalNoteId: noteId,
        text: payload?.text,
        careTime: payload?.care_time,
        careDayOffset: payload?.care_day_offset,
      });
      if (!addendum.addendum_text) return prev;
      const nextNote = attachAddendumToNote(target, addendum);
      saved = nextNote;
      const updated = prev.map((n, i) => (i === index ? nextNote : n));
      saveJson(SESSION_KEY, updated);
      return updated;
    });
    return saved;
  }, []);

  const getPatientCarePlans = useCallback(
    (patientId) => (patientId ? carePlansByPatient?.[patientId] || [] : []),
    [carePlansByPatient]
  );

  const saveCarePlan = useCallback((patientId, noteContent, metadata = {}) => {
    if (!patientId || !noteContent) return null;
    const record = createCarePlanRecord({
      noteContent,
      documentationTime: metadata.documentationTime,
      careTime: metadata.care_time,
      careDayOffset: metadata.care_day_offset,
    });
    setCarePlansByPatient((prev) => {
      const current = prev?.[patientId] || [];
      const updated = {
        ...(prev || {}),
        [patientId]: [...current, record],
      };
      saveJson(CARE_PLANS_KEY, updated);
      return updated;
    });
    setDocumentedPatientIds((prev) => {
      const next = prev.includes(patientId) ? prev : [...prev, patientId];
      saveJson(PATIENT_STATUS_KEY, next);
      return next;
    });
    return record;
  }, []);

  const ensureCarePlanInterventionTasks = useCallback(() => {
    // Care-plan interventions are documentation only — never spawn patient tasks.
  }, []);

  const addCarePlanAddendum = useCallback((patientId, carePlanId, payload) => {
    if (!patientId || !carePlanId) return null;
    let saved = null;
    setCarePlansByPatient((prev) => {
      const current = prev?.[patientId] || [];
      const index = current.findIndex((plan) => plan.id === carePlanId);
      if (index < 0) return prev;
      const addendum = createCarePlanAddendum({
        carePlanId,
        text: payload?.text,
        careTime: payload?.care_time,
        careDayOffset: payload?.care_day_offset,
      });
      if (!addendum.addendum_text) return prev;
      const nextPlan = attachAddendumToCarePlan(current[index], addendum);
      saved = nextPlan;
      const nextPlans = current.map((plan, i) => (i === index ? nextPlan : plan));
      const updated = { ...(prev || {}), [patientId]: nextPlans };
      saveJson(CARE_PLANS_KEY, updated);
      return updated;
    });
    return saved;
  }, []);

  const resolveCarePlan = useCallback((patientId, carePlanId) => {
    if (!patientId || !carePlanId) return null;
    let saved = null;
    setCarePlansByPatient((prev) => {
      const current = prev?.[patientId] || [];
      const index = current.findIndex((plan) => plan.id === carePlanId);
      if (index < 0) return prev;
      const nextPlan = {
        ...current[index],
        status: 'resolved',
        resolved_at: new Date().toISOString(),
      };
      saved = nextPlan;
      const nextPlans = current.map((plan, i) => (i === index ? nextPlan : plan));
      const updated = { ...(prev || {}), [patientId]: nextPlans };
      saveJson(CARE_PLANS_KEY, updated);
      return updated;
    });
    return saved;
  }, []);

  const saveNote = useCallback(
    (note) => {
      const noteType = normalizeNoteType(note.note_type);
      const noteTimestamp = note.datetime || new Date().toISOString();
      console.log('[TaskDebug] saveNote:start', {
        noteId: note.id,
        patientId: note.patient_id,
        noteType,
        noteTimestamp,
      });
      setSessionNotes((prev) => {
        const existing = prev.findIndex((n) => n.id === note.id);
        const updated = existing >= 0 ? prev.map((n, i) => (i === existing ? note : n)) : [...prev, note];
        saveJson(SESSION_KEY, updated);
        return updated;
      });
      if (note.patient_id) {
        setDocumentedPatientIds((prev) => {
          const next = prev.includes(note.patient_id) ? prev : [...prev, note.patient_id];
          saveJson(PATIENT_STATUS_KEY, next);
          return next;
        });

        // 1) complete matching recurring task (if any)
        checkAndCompleteTask(note.patient_id, noteType, noteTimestamp, note.id, note);

        const noteContent = getNoteContent(note);
        console.log('[TaskDebug] saveNote:noteContentVitals', noteContent?.vitals || null);
        const contentCompletedIds = getTaskIdsCompletedByNote(
          tasksByPatient?.[note.patient_id] || [],
          noteType,
          noteContent,
          new Date(noteTimestamp)
        );
        console.log('[TaskDebug] saveNote:contentCompletedIds', contentCompletedIds);
        contentCompletedIds.forEach((taskId) => {
          console.log('[TaskDebug] saveNote:completeFromContent', { taskId, noteId: note.id });
          completeTask(note.patient_id, taskId, noteTimestamp, note.id);
        });

        if (note.manualTaskCompletionTaskId) {
          const meds = noteContent?.medications_administered?.length
            ? noteContent.medications_administered
            : noteContent?.medications || [];
          const notGivenMed = meds.find((med) => med?.not_given);
          const completionMeta =
            note.completion_outcome === 'not_given' || notGivenMed
              ? {
                  completionOutcome: 'not_given',
                  deviationReason:
                    note.deviation_reason
                    || notGivenMed?.deviation_reason
                    || null,
                }
              : null;
          completeTask(
            note.patient_id,
            note.manualTaskCompletionTaskId,
            noteTimestamp,
            note.id,
            completionMeta
          );
        }

        // Outstanding tasks from recordings are note content only — never create patient tasks.
      }
      if (note.clinicalIds?.noteId) {
        saveClinicalNoteMetrics(note.clinicalIds, note, note.pilotMetrics || {});
      } else {
        persistClinicalNote(note, {
          ...(note.pilotMetrics || {}),
          wasEdited: note.pilotMetrics?.wasEdited,
          editCount: note.pilotMetrics?.editCount,
          wordsBeforeEdit: note.pilotMetrics?.wordsBeforeEdit,
          wordsAfterEdit: note.pilotMetrics?.wordsAfterEdit,
          timeToReviewSeconds: note.pilotMetrics?.timeToReviewSeconds,
          recordingEndAt: note.recordingEndAt,
          noteGeneratedAt: note.pilotMetrics?.noteGeneratedAt,
        });
      }
    },
    [checkAndCompleteTask, completeTask]
  );

  const deleteNote = useCallback((noteId) => {
    setSessionNotes((prev) => {
      const updated = prev.filter((n) => n.id !== noteId);
      saveJson(SESSION_KEY, updated);
      return updated;
    });
  }, []);

  const updatePdfSaved = useCallback((noteId, saved) => {
    setSessionNotes((prev) => {
      const updated = prev.map((n) => (n.id === noteId ? { ...n, pdf_saved: saved } : n));
      saveJson(SESSION_KEY, updated);
      return updated;
    });
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = new Date();
      setTasksByPatient((prev) => {
        let changed = false;
        const next = { ...(prev || {}) };
        Object.keys(next).forEach((patientId) => {
          const tasks = next[patientId] || [];
          const updated = tasks.map((t) => {
            if (t.status !== 'pending') return t;
            if (!t.windowEnd) return t;
            if (new Date(t.windowEnd) < now) {
              changed = true;
              return { ...t, status: 'overdue' };
            }
            return t;
          });
          next[patientId] = updated;
        });
        if (!changed) return prev;
        saveJson(TASKS_KEY, next);
        return next;
      });
    }, 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const toggleFavorite = useCallback((patientId) => {
    setFavoriteIds((prev) => {
      const next = prev.includes(patientId)
        ? prev.filter((id) => id !== patientId)
        : [...prev, patientId];
      saveJson(FAVORITES_KEY, next);
      return next;
    });
  }, []);

  const isFavorite = useCallback((patientId) => favoriteIds.includes(patientId), [favoriteIds]);

  const isDocumented = useCallback(
    (patientId) => documentedPatientIds.includes(patientId),
    [documentedPatientIds]
  );

  const reports = useMemo(() => {
    const sessionReports = sessionNotes
      .filter((n) => ['handover', 'discharge'].includes(n.note_type))
      .map((n) => ({
        id: `report-${n.id}`,
        noteId: n.id,
        type: n.note_type === 'handover' ? 'shift_handover' : n.note_type,
        title: `${n.patient_name || 'Patient'}`,
        patientName: n.patient_name,
        patientId: n.patient_id,
        datetime: n.datetime,
        note: n,
      }));
    return sessionReports;
  }, [sessionNotes]);

  const vitalsNotes = useMemo(
    () => sessionNotes.filter((n) => n.note_type === 'vitals'),
    [sessionNotes]
  );

  const progressNotes = useMemo(
    () => sessionNotes.filter((n) => n.note_type === 'progress_note'),
    [sessionNotes]
  );

  const medicationsNotes = useMemo(
    () => sessionNotes.filter((n) => n.note_type === 'medication_admin'),
    [sessionNotes]
  );

  const nurse = useMemo(() => {
    const baseName = nurseName || MOCK_NURSE.nameEn;
    return {
      id: MOCK_NURSE.id,
      name: getNurseDisplayName(baseName, language),
      nameEn: baseName,
      initials: getInitials(baseName),
      role: t('registeredNurse'),
      hospital: t('hospitalName'),
    };
  }, [nurseName, language, t]);

  const value = useMemo(
    () => ({
      sessionNotes,
      saveNote,
      addAddendum,
      deleteNote,
      updatePdfSaved,
      favoriteIds,
      toggleFavorite,
      isFavorite,
      isDocumented,
      documentedPatientIds,
      vitalsNotes,
      progressNotes,
      medicationsNotes,
      reports,
      nurse,
      isRecordingActive,
      setIsRecordingActive,
      draftGeneration,
      setDraftGeneration,
      getFlowSheetData,
      addFluidInput,
      addFluidOutput,
      markFluidBalanceTaskComplete,
      addManualMedication,
      addManualObservation,
      admittedPatients,
      addAdmittedPatient,
      shiftHandoverDrafts,
      setShiftHandoverDrafts,
      updatePatientHandoverDraft,
      clearShiftHandoverDrafts,
      markAllPatientsShiftEnded,
      handoverRecordingTarget,
      setHandoverRecordingTarget,
      clearHandoverRecordingTarget,
      toast,
      showToast,
      clearToast,
      getPatientTasks,
      addTaskToPatient,
      completeTask,
      setPatientTasks,
      ensureDefaultRecurringTasks,
      getPatientCarePlans,
      carePlansByPatient,
      saveCarePlan,
      ensureCarePlanInterventionTasks,
      addCarePlanAddendum,
      resolveCarePlan,
      taskDocSession,
      openTaskDocumentation,
      setTaskDocumentationDraft,
      setTaskDocumentationNotes,
      closeTaskDocumentation,
      clearTaskDocumentation,
      registerTaskDocTranscriptHandler,
      submitTaskDocTranscript,
    }),
    [
      sessionNotes,
      saveNote,
      addAddendum,
      deleteNote,
      updatePdfSaved,
      favoriteIds,
      toggleFavorite,
      isFavorite,
      isDocumented,
      documentedPatientIds,
      vitalsNotes,
      progressNotes,
      medicationsNotes,
      reports,
      nurse,
      isRecordingActive,
      draftGeneration,
      getFlowSheetData,
      addFluidInput,
      addFluidOutput,
      markFluidBalanceTaskComplete,
      addManualMedication,
      addManualObservation,
      admittedPatients,
      addAdmittedPatient,
      shiftHandoverDrafts,
      setShiftHandoverDrafts,
      updatePatientHandoverDraft,
      clearShiftHandoverDrafts,
      markAllPatientsShiftEnded,
      handoverRecordingTarget,
      clearHandoverRecordingTarget,
      toast,
      showToast,
      clearToast,
      getPatientTasks,
      addTaskToPatient,
      completeTask,
      setPatientTasks,
      ensureDefaultRecurringTasks,
      getPatientCarePlans,
      carePlansByPatient,
      saveCarePlan,
      ensureCarePlanInterventionTasks,
      addCarePlanAddendum,
      resolveCarePlan,
      taskDocSession,
      openTaskDocumentation,
      setTaskDocumentationDraft,
      setTaskDocumentationNotes,
      closeTaskDocumentation,
      clearTaskDocumentation,
      registerTaskDocTranscriptHandler,
      submitTaskDocTranscript,
    ]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
